import { useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';

import { openQuickFilterAtom } from 'Atoms/App/Plp/QuickFilterOpenAtom';
import type { Item } from 'Components/MultiSelect/MultiSelect.entity';
import { FilterLink } from 'Components/Plp/Filters/Components/FilterLink/FilterLink';
import type {
  PlpFiltersMessagesKeys,
  PlpFiltersMessagesProps,
} from 'Components/Plp/Filters/FilterPanel.entity';
import { trackFilterEvent } from 'Components/Plp/Filters/Filters.tracking';
import { getArrayFilterValue } from 'Utils/SearchFilters/GetArrayFilterValue';

import { useFilterLink } from './UseFilterLink';
import { useUpdateFilters } from './UseUpdateFilters';

const OTHER_LABELS = ['other', 'others', 'autres', 'autre'];

interface MultiSelectOption {
  id: string;
  label: string;
  selected: boolean;
  count: number;
  iconName?: string;
  labelKey?: string;
  renderLabel?: (name: string) => JSX.Element | React.ReactNode;
}

const useMultiSelectFilters = (
  filterName: FilterKey,
  options: MultiSelectOption[],
  strings: PlpFiltersMessagesProps,
  locale: Locale,
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
  listEnhanceFn?: (optObj: MultiSelectOption, selected: string[]) => Item,
  applyButton?: boolean,
) => {
  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);
  const filtersList = useAtomValue(filtersListAtom);
  const { updateFilters } = useUpdateFilters();
  const { getLinkForFilterValue } = useFilterLink({
    filterId: filterName,
    locale,
    isPlpFiltersSeoEnabled,
    tenant,
  });
  const [pendingSelections, setPendingSelections] = useState<Record<string, boolean>>({});

  const defaultListEnhanceFn = (optObj: MultiSelectOption, selected: string[]) => {
    const labelKey = optObj.labelKey ?? optObj.id;
    const intlLabel = strings[labelKey as PlpFiltersMessagesKeys] || optObj.label;
    const link = getLinkForFilterValue({
      filterValue: optObj.id,
      resultCount: optObj.count,
    });

    return {
      renderLabel: <FilterLink link={link}>{intlLabel}</FilterLink>,
      ...optObj,
      label: intlLabel,
      // name: filterName,
      selected:
        Array.isArray(optObj.id) && selected.length > 0
          ? optObj.id.every(item => selected.includes(item))
          : selected?.includes(optObj.id),
    } as Item;
  };

  const selectedItems = getArrayFilterValue(filterName as FilterKey, filtersList) || [];

  const updateSelections = (ids: string[], isChecked: boolean) => {
    let updatedSelections = [...selectedItems];
    if (isChecked) {
      updatedSelections.push(...ids.filter(filterId => !updatedSelections.includes(filterId)));
    } else {
      updatedSelections = updatedSelections.filter(item => !ids.includes(item));
    }
    return updatedSelections;
  };

  const applyPendingSelections = (ids: string[], isChecked: boolean) => {
    setPendingSelections(prev => {
      const newSelections = { ...prev };
      ids.forEach(filterId => (newSelections[filterId] = isChecked));
      return newSelections;
    });
  };

  const updateFiltersAndTrack = (newItems: string[]) => {
    trackFilterEvent(filterName, newItems, filtersList);
    updateFilters([{ id: filterName, value: newItems }]);
  };

  const handleSelect = (id: string, isChecked: boolean) => {
    const filter = options.find(opt => opt.id.includes(id));
    if (filter) {
      const filterIds = Array.isArray(filter.id) ? filter.id : [filter.id];
      const updatedSelections = updateSelections(filterIds, isChecked);

      applyButton
        ? applyPendingSelections(filterIds, isChecked)
        : updateFiltersAndTrack(updatedSelections);
    } else {
      const updatedSelections = isChecked
        ? [...selectedItems, id]
        : selectedItems.filter(item => item !== id);

      applyButton
        ? setPendingSelections(prev => ({ ...prev, [id]: isChecked }))
        : updateFiltersAndTrack(updatedSelections);
    }
  };

  const handleMultiSelect = (selections: Record<string, boolean>) => {
    let updatedSelections = [...selectedItems];

    Object.entries(selections).forEach(([id, isChecked]) => {
      if (isChecked && !updatedSelections.includes(id)) {
        updatedSelections.push(id);
      } else if (!isChecked && updatedSelections.includes(id)) {
        updatedSelections = updatedSelections.filter(item => item !== id);
      }
    });
    trackFilterEvent(filterName, [...Object.values(updatedSelections)], filtersList);
    updateFilters([{ id: filterName, value: updatedSelections }]);
  };

  const listWithSelected = options
    .map(optObj =>
      listEnhanceFn
        ? listEnhanceFn({ ...optObj }, selectedItems)
        : defaultListEnhanceFn(optObj, selectedItems),
    )
    .sort((a, b) => {
      if (OTHER_LABELS.includes(a.label.toLowerCase())) return 1;
      if (OTHER_LABELS.includes(b.label.toLowerCase())) return -1;
      return a.label > b.label ? 1 : -1;
    });

  const handleApply = () => {
    handleMultiSelect(pendingSelections);
    setPendingSelections({});
    setOpenQuickFilter(null);
  };

  return {
    handleSelect,
    handleMultiSelect,
    handleApply,
    listWithSelected,
  };
};

export { useMultiSelectFilters };
export type { MultiSelectOption };
